import riveFile from '~/assets/reward-flow.riv'

import { Dialog } from '@kobalte/core/dialog'
import { Fit, Layout, Rive } from '@rive-app/canvas'
import {
  createEffect,
  createSignal,
  type JSXElement,
  Match,
  onCleanup,
  onMount,
  Show,
  Switch,
} from 'solid-js'
import { Motion, type MotionComponentProps, Presence } from 'solid-motionone'
import { cn } from '~/lib/utils'
import { m } from '~/paraglide/messages'
import { type Item, rewardFlow } from '~/store/reward'

interface Props {
  background?: string
}

export function RewardFlow(props: Props) {
  const title = () => {
    const item = rewardFlow.current
    if (!item) return ''
    if (item.type === 'fallback') {
      return m.duplicate_item_reclaimed()
    }
    if (item.type === 'tree') {
      return m.new_plant_unlocked()
    }
    return m.claimed_title()
  }

  const itemLabel = () => {
    const item = rewardFlow.current
    if (!item) return ''
    switch (item.type) {
      case 'coin':
        return `${m.coin()}x${item.amount}`
      case 'gem':
        return `${m.time_crystal()}x${item.amount}`
      case 'sound':
      case 'tree':
        return item.title
      case 'fallback':
        return `${item.fallback.type === 'coin' ? m.coin() : m.time_crystal()}x${(item.fallback as Extract<Item, { type: 'coin' | 'gem' }>).amount}`
      default:
        return ''
    }
  }

  return (
    <Dialog open={!!rewardFlow.items}>
      <Dialog.Portal>
        <Dialog.Content
          class={cn(
            'data-expanded:animate-in data-closed:animate-out data-closed:fade-out-0 data-expanded:fade-in-0 data-closed:zoom-out-95 data-expanded:zoom-in-95 text-white fixed inset-0 z-50 bg-pink-500 pt-[var(--safe-area-top)] duration-200',
          )}
          style={{
            background: props.background,
          }}
        >
          <Show
            when={!rewardFlow.previewScene}
            fallback={rewardFlow.previewScene}
          >
            <div class="absolute top-[50%] flex w-full translate-y-[-50%] flex-col justify-center text-center">
              <Show when={!rewardFlow.isNextBtnLocked}>
                <h3 class="text-xl font-medium">{title()}</h3>
              </Show>
              <Presence initial exitBeforeEnter>
                <Show when={rewardFlow.current} keyed>
                  {(item) => {
                    switch (item.type) {
                      case 'fallback':
                        return <FallbackItem item={item} />
                      case 'sound':
                      case 'tree':
                      case 'coin':
                      case 'gem':
                      case 'coupon':
                        return <Item item={item} />
                      default:
                        return <></>
                    }
                  }}
                </Show>
              </Presence>
              <Show when={!rewardFlow.isNextBtnLocked}>
                <p class="text-shadow-(--foreground-shadow) text-[30px] font-bold">
                  {itemLabel()}
                </p>
              </Show>
            </div>

            <button
              class="absolute inset-0 flex flex-col items-center justify-end pb-[10vh]"
              onClick={rewardFlow.next}
              disabled={rewardFlow.isNextBtnLocked}
            >
              <Show when={!rewardFlow.isNextBtnLocked}>
                <p class="animate-pulse text-sm">{m.claimed_next()}</p>
              </Show>
            </button>
          </Show>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>
  )
}

function Item(props: { item: Exclude<Item, { type: 'fallback' }> }) {
  const [hasEntered, setHasEntered] = createSignal(false)

  let canvas!: HTMLCanvasElement
  let rive!: Rive

  onMount(() => {
    rive = new Rive({
      canvas: canvas,
      src: riveFile,
      artboard: 'reward_claimed',
      layout: new Layout({ fit: Fit.FitHeight }),
      stateMachines: ['State Machine 1'],
      onLoad: () => {
        const [name] = rive.stateMachineNames
        const [input] = rive.stateMachineInputs(name)
        input.value = props.item.type === 'tree' ? 2 : 1
      },
    })
  })
  createEffect(() => {
    if (!hasEntered()) return
    rewardFlow.canNext()
    rive.resizeDrawingSurfaceToCanvas()
    rive.play()
  })
  onCleanup(() => {
    rive?.cleanup()
  })
  return (
    <Motion.div
      class="relative"
      initial={{ scale: 0.3, opacity: 0.3 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ duration: 0.5, easing: [0.3, 0, 0.8, 0.15] }}
      onMotionComplete={({ detail }) => {
        if (detail.target.scale === 1) {
          setHasEntered(true)
        }
      }}
    >
      <canvas class="h-[375px] w-full" ref={canvas} />
      <Icon icon={props.item.icon} />
    </Motion.div>
  )
}

function FallbackItem(props: { item: Extract<Item, { type: 'fallback' }> }) {
  const [hasEntered, setHasEntered] = createSignal(false)
  const [toggle, setToggle] = createSignal(false)

  let canvas!: HTMLCanvasElement
  let rive!: Rive

  onMount(() => {
    rive = new Rive({
      canvas: canvas,
      src: riveFile,
      artboard: 'reward_repeat',
      layout: new Layout({ fit: Fit.FitHeight }),
      stateMachines: ['State Machine 1'],
    })
  })
  createEffect(() => {
    if (!hasEntered()) return
    setToggle(true)
    rive.resizeDrawingSurfaceToCanvas()
    rive.play()
    setTimeout(() => {
      rive.reset({
        artboard: 'reward_claimed',
        stateMachines: ['State Machine 1'],
      })
      const [name] = rive.stateMachineNames
      const [input] = rive.stateMachineInputs(name)
      input.value = props.item.from.type === 'tree' ? 2 : 1
      rive.play()
      rewardFlow.canNext()
    }, 1000)
  })
  onCleanup(() => {
    rive?.cleanup()
  })
  return (
    <Motion.div
      class="relative"
      initial={{ scale: 0.3, opacity: 0.5 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ duration: 0.5, easing: [0.3, 0, 0.8, 0.15] }}
      onMotionComplete={({ detail }) => {
        if (detail.target.opacity === 1) {
          setHasEntered(true)
        }
      }}
    >
      <canvas class="h-[375px] w-full" ref={canvas} />
      <Presence initial={false} exitBeforeEnter>
        <Show
          when={toggle()}
          keyed
          fallback={
            <Icon
              icon={props.item.from.icon}
              motionProps={{
                exit: {
                  filter: 'brightness(3) contrast(3) saturate(0)',
                  opacity: 0,
                },
                transition: {
                  duration: 1,
                  easing: 'ease-in-out',
                  opacity: { delay: 0.75, duration: 0.25 },
                },
              }}
            />
          }
        >
          {(_item) => (
            <Icon
              icon={props.item.fallback.icon}
              motionProps={{
                initial: { opacity: 0 },
                animate: { opacity: 1 },
                transition: { duration: 0.3, easing: 'ease-in-out' },
              }}
            />
          )}
        </Show>
      </Presence>
    </Motion.div>
  )
}

function Icon(props: {
  icon: string | JSXElement
  motionProps?: MotionComponentProps
}) {
  return (
    <Switch>
      <Match when={typeof props.icon === 'string'}>
        <Motion.img
          {...props.motionProps}
          class="absolute left-[50%] top-[50%] size-[150px] translate-x-[-50%] translate-y-[-50%]"
          src={props.icon as string}
        />
      </Match>
      <Match when={typeof props.icon === 'object'}>
        <Motion.div
          {...props.motionProps}
          class="absolute left-[50%] top-[50%] grid size-[150px] translate-x-[-50%] translate-y-[-50%] place-items-center"
        >
          {props.icon}
        </Motion.div>
      </Match>
    </Switch>
  )
}
